/**
 * `AudioEngine` — the one stateful object that owns a real `AudioContext`.
 * Everything above it (`sequencer.ts`, `danger.ts`, `gain.ts`) is pure and
 * unit-tested directly; this class is the thin, deliberately untested-beyond-
 * "does not throw" layer that wires them to actual sound.
 *
 * Autoplay policy (`docs/DESIGN.md`'s owner brief: "the AudioContext starts
 * only on a user gesture"): `start()` must be called from inside a click
 * handler — `audio/bootstrap.ts`'s `startAudioForGesture` is that call site.
 * Every other method is safe to call at any time, gesture or not, including
 * before `start()` has ever run and in an environment with no Web Audio API
 * at all (a headless test runner, a browser with it disabled): nothing here
 * throws past its own `try`/`catch`, because the game has to stay fully
 * playable with sound simply absent.
 */
import { COMPOSITION } from './composition.js';
import type { DrumHit, NoteEvent } from './composition.js';
import { selectMusicLayer } from './danger.js';
import { clampVolume, effectiveGain } from './gain.js';
import { MusicScheduler } from './sequencer.js';
import type { AudioClock } from './sequencer.js';
import { triggerSfx } from './sfx.js';
import type { SfxName, SfxOptions } from './sfx.js';
import { playHat, playKick, playSnare, playVoiceNote } from './voices.js';
import { noteFreq } from './notes.js';

/** How far ahead the scheduler looks, in seconds. Comfortably more than one `TICK_INTERVAL_MS` so a slow tab never leaves a gap. */
const LOOKAHEAD_SEC = 0.12;
/** How often the lookahead timer fires. The classic Web Audio scheduling pattern — this decides only when to *check*, never when a note *plays*. */
const TICK_INTERVAL_MS = 25;

function resolveAudioContextCtor(): (new () => AudioContext) | null {
  if (typeof window === 'undefined') return null;
  const withVendorPrefix = window as unknown as { webkitAudioContext?: new () => AudioContext };
  return window.AudioContext ?? withVendorPrefix.webkitAudioContext ?? null;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private scheduler: MusicScheduler | null = null;
  private lookaheadTimer: ReturnType<typeof setInterval> | null = null;

  private musicEnabled = true;
  private musicVolume = 0.55; // "moderate volume" — the owner's default.
  private sfxEnabled = true;
  private sfxVolume = 0.8;
  private dangerFraction = 0;

  /** Whether a real `AudioContext` exists — false in an environment without Web Audio, or before the first gesture. */
  get available(): boolean {
    return this.ctx !== null;
  }

  get currentMusicGain(): number {
    return effectiveGain(this.musicEnabled, this.musicVolume);
  }

  get currentSfxGain(): number {
    return effectiveGain(this.sfxEnabled, this.sfxVolume);
  }

  /**
   * Create the `AudioContext` and its graph. Must be called from a user
   * gesture the first time; every call after the first is a cheap no-op
   * check plus a `resume()`. Never throws — a browser that blocks or lacks
   * Web Audio entirely leaves `available` false and the game runs silent.
   */
  start(): void {
    if (this.ctx !== null) {
      void this.resume();
      return;
    }
    try {
      const Ctor = resolveAudioContextCtor();
      if (Ctor === null) return;
      const ctx = new Ctor();
      const masterGain = ctx.createGain();
      masterGain.connect(ctx.destination);
      const musicGain = ctx.createGain();
      musicGain.gain.value = this.currentMusicGain;
      musicGain.connect(masterGain);
      const sfxGain = ctx.createGain();
      sfxGain.gain.value = this.currentSfxGain;
      sfxGain.connect(masterGain);

      this.ctx = ctx;
      this.musicGain = musicGain;
      this.sfxGain = sfxGain;

      const clock: AudioClock = { now: () => ctx.currentTime };
      this.scheduler = new MusicScheduler(COMPOSITION, clock, (event, startSec, durationSec) => {
        this.playComposedEvent(event, startSec, durationSec);
      });
      this.scheduler.setLayer(selectMusicLayer(this.dangerFraction, 'base'));
    } catch {
      // Web Audio blocked or unsupported. The game stays fully playable.
      this.ctx = null;
    }
  }

  async resume(): Promise<void> {
    if (this.ctx === null) return;
    try {
      if (this.ctx.state === 'suspended') await this.ctx.resume();
    } catch {
      // Autoplay policy refused it — a later gesture will succeed instead.
    }
  }

  async suspend(): Promise<void> {
    if (this.ctx === null) return;
    try {
      if (this.ctx.state === 'running') await this.ctx.suspend();
    } catch {
      /* nothing to do — the tab is already going away or the context is already stopped. */
    }
  }

  /** Start the lookahead timer that actually plays the music loop. Idempotent. Menus never call this — see `docs/decisions/0042`. */
  startMusic(): void {
    if (this.ctx === null || this.lookaheadTimer !== null) return;
    // The audio clock ran on while the timer was stopped; start counting from
    // now rather than treating the whole gap as playback to catch up on.
    this.scheduler?.reset();
    this.lookaheadTimer = setInterval(() => {
      this.scheduler?.tick(LOOKAHEAD_SEC);
    }, TICK_INTERVAL_MS);
  }

  /** Stop scheduling new notes. Whatever is already scheduled finishes naturally rather than cutting off mid-note. */
  stopMusic(): void {
    if (this.lookaheadTimer !== null) {
      clearInterval(this.lookaheadTimer);
      this.lookaheadTimer = null;
    }
  }

  setMusicEnabled(on: boolean): void {
    this.musicEnabled = on;
    this.applyMusicGain();
  }

  setMusicVolume(volume: number): void {
    this.musicVolume = clampVolume(volume);
    this.applyMusicGain();
  }

  setSfxEnabled(on: boolean): void {
    this.sfxEnabled = on;
    this.applySfxGain();
  }

  setSfxVolume(volume: number): void {
    this.sfxVolume = clampVolume(volume);
    this.applySfxGain();
  }

  /** `fraction`: `danger.ts`'s `boardDangerFraction`, 0..1. Presentation-only — never read back by anything simulation-affecting. */
  setDanger(fraction: number): void {
    this.dangerFraction = clampVolume(fraction);
    const next = selectMusicLayer(this.dangerFraction, this.scheduler?.currentLayer ?? 'base');
    this.scheduler?.setLayer(next);
  }

  playSfx(name: SfxName, opts?: SfxOptions): void {
    if (this.ctx === null || this.sfxGain === null || !this.sfxEnabled) return;
    try {
      triggerSfx(this.ctx, this.sfxGain, name, this.ctx.currentTime, opts);
    } catch {
      // A synthesis failure must never break the game it is decorating.
    }
  }

  private applyMusicGain(): void {
    if (this.musicGain === null || this.ctx === null) return;
    this.musicGain.gain.setTargetAtTime(this.currentMusicGain, this.ctx.currentTime, 0.02);
  }

  private applySfxGain(): void {
    if (this.sfxGain === null || this.ctx === null) return;
    this.sfxGain.gain.setTargetAtTime(this.currentSfxGain, this.ctx.currentTime, 0.02);
  }

  private playComposedEvent(
    event: NoteEvent | DrumHit,
    startSec: number,
    durationSec: number,
  ): void {
    if (this.ctx === null || this.musicGain === null) return;
    try {
      if ('drum' in event) {
        this.playDrum(event, startSec);
        return;
      }
      playVoiceNote(this.ctx, this.musicGain, {
        voice: event.voice,
        freq: noteFreq(event.note, event.octave),
        startSec,
        durationSec,
        velocity: event.velocity,
      });
    } catch {
      // One missed note is inaudible; a thrown error mid-loop is not worth the risk.
    }
  }

  private playDrum(hit: DrumHit, startSec: number): void {
    if (this.ctx === null || this.musicGain === null) return;
    switch (hit.drum) {
      case 'kick':
        playKick(this.ctx, this.musicGain, startSec, hit.velocity);
        return;
      case 'snare':
        playSnare(this.ctx, this.musicGain, startSec, hit.velocity);
        return;
      case 'hat':
        playHat(this.ctx, this.musicGain, startSec, hit.velocity);
        return;
    }
  }
}

/** The one shared engine instance every screen reads and writes. */
export const audio = new AudioEngine();
